import React, { useState, useCallback, useRef, forwardRef } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { useClickOutside, mergeClassNames, useIsTouchDevice } from '../../../../../utils';
import TransitionReveal from '../../../TransitionReveal';
import ArrowDownIcon from './assets/arrow-down_16.svg';
import styles from './index.module.scss';

export const SelectField = forwardRef(SelectFieldImpl);
/**
 * SelectField is a component to hijack the clickable space from a
 * native select input field to be able to use the native select UI's on touch
 * capable devices, but use custom styled input on other devices.
 *
 * @example
 * ```javascript
 * const options = [
 *   {value: '', displayValue: 'Default placeholder text'},
 *   {value: 'id1', displayValue: 'Visible text for `id1`'},
 *   {value: 'id2', displayValue: 'Visible text for `id2`'}
 * ]
 * <SelectField
 *   options={options}
 *   handleSelectChange={(event, value) => {
 *     if (nativeSelectRef.current) nativeSelectRef.current.value = value;
 *   }}
 *   handleNativeSelectChange={event => {
 *     handleSelectChange(event.target.value); // if extending the <select> functionality is needed..
 *   }}
 * />
 * ```
 * @param {Object} props SelectField props
 * @param {string} props.id Select id
 * @param {string} props.name Select name
 * @param {string} [props.defaultValue] Select value
 * @param {React.MutableRefObject<HTMLInputElement>} [props.ref] Ref forwarded to th select field
 * @param {Array<{value: string, displayValue: string, disabled?: boolean}>} props.options Array of selectable elements
 * @param {React.ReactNode} [props.children] Optional element to be wrapped within this class
 * @param {(event?: MouseEvent) => void} [props.onChange] Function to be called on select
 * @param {(value: string) => string} [props.handleSelectChangeCallback] Function to be called on select
 * @param {(value: string, event?: MouseEvent) => string} [props.handleNativeSelectChangeCallback] Function to be called when an item is selected in the native select element. Defaults to handleSelectChange
 * @param {boolean} [props.disableHijack] Disables hijack of native toggle if true
 * @param {string} [props.className] Class to be appended to elem's classes
 * @param {React.CSSProperties} [props.style] Inline style
 * @param {string} [props.nativeSelectClassname] Class to alter the native select element
 * @param {React.CSSProperties} [props.nativeSelectStyle] Inline style to alter the native select element
 * @param {string} [props.hijackTriggerClassname] Class to alter the select custom hijack trigger
 * @param {React.CSSProperties} [props.hijackTriggerStyle] Inline style to alter the select custom hijack trigger
 */
export default function SelectFieldImpl(
  {
    id,
    name = null,
    options,
    children = null,
    defaultValue = null,
    onChange = () => null,
    handleSelectChangeCallback = null,
    handleNativeSelectChangeCallback = handleSelectChangeCallback,
    disableHijack = false,
    className = null,
    style = null,
    nativeSelectClassname = null,
    nativeSelectStyle = null,
    hijackTriggerClassname = null,
    hijackTriggerStyle = null,
  },
  ref
) {
  defaultValue = defaultValue || options?.[0]?.value;
  const [isOpen, setIsOpen] = useState(false);
  const { isTouchDevice } = useIsTouchDevice();
  const nativeSelectRef = useRef(null);
  const clickOutsideRef = useClickOutside(() => setIsOpen(false));

  const isDisplayingPlaceholder = !nativeSelectRef.current || nativeSelectRef.current.value === '';

  const refHandler = useCallback(
    node => {
      nativeSelectRef.current = node;
      if (ref?.current) ref.current = node;
      if (typeof ref === 'function') ref(node);
    },
    [ref]
  );

  const toggleOptions = useCallback(() => {
    setIsOpen(state => !state);
  }, []);

  const handleSelectChange = useCallback(value => {
    if (nativeSelectRef.current) nativeSelectRef.current.value = value;
  }, []);

  const handleCustomSelectChange = useCallback(
    value => {
      toggleOptions();
      value = handleSelectChangeCallback ? handleSelectChangeCallback(value) : value;
      handleSelectChange(value);
    },
    [handleSelectChange, handleSelectChangeCallback, toggleOptions]
  );

  const handleNativeSelectChange = useCallback(
    event => {
      let { value } = event.target;
      value = handleNativeSelectChangeCallback ? handleNativeSelectChangeCallback(value, event) : value;
      handleSelectChange(value);
    },
    [handleNativeSelectChangeCallback, handleSelectChange]
  );

  const handledEventlessSelect = useCallback(
    value => {
      onChange({ target: { name, value } });
      handleCustomSelectChange(value);
    },
    [handleCustomSelectChange, name, onChange]
  );

  return (
    <div className={mergeClassNames([styles.selectField, className])} style={style} ref={clickOutsideRef}>
      {children}
      <select
        id={id}
        name={name}
        ref={refHandler}
        onChange={event => handleNativeSelectChange(event)}
        className={mergeClassNames([
          styles.selectNative,
          nativeSelectClassname ? nativeSelectClassname : null,
          isDisplayingPlaceholder ? styles.selectNativePlaceholder : null,
        ])}
        style={nativeSelectStyle}
        defaultValue={defaultValue}
      >
        {options.map(({ value, displayValue, disabled = false }) => (
          <option key={value} value={value} disabled={disabled}>
            {displayValue}
          </option>
        ))}
      </select>
      <div
        role="button"
        onClick={toggleOptions}
        className={mergeClassNames([
          styles.customSelectTrigger,
          !disableHijack ? styles.customSelectTriggerHijack : null,
          isTouchDevice ? styles.touch : null,
          hijackTriggerClassname ? hijackTriggerClassname : null,
        ])}
        style={hijackTriggerStyle}
      />
      <div className={styles.customSelect}>
        <Options options={options} onSelect={handledEventlessSelect} isOpen={isOpen} />
      </div>
      <AnimatedArrowDown isTurned={isOpen} className={styles.inlineIcon} />
    </div>
  );
}

function Options({ options, onSelect, isOpen }) {
  return (
    <TransitionReveal
      isVisible={isOpen}
      className={styles.optionsOuter}
      initialTranslate={{ y: '-300px' }}
      initialOpacity={1}
    >
      <ul className={styles.options}>
        <li role="presentation" className={mergeClassNames(styles.option, styles.disabled)}>
          placeholder
        </li>
        {options.map((option, index) => (
          <li
            key={option.displayValue}
            role="button"
            className={mergeClassNames(
              styles.option,
              option.disabled ? styles.disabled : null,
              option.disabled && index === 0 ? styles.hidden : null
            )}
            onClick={!option.disabled ? event => onSelect(option.value, event) : null}
          >
            {option.displayValue}
          </li>
        ))}
      </ul>
    </TransitionReveal>
  );
}

function AnimatedArrowDown({ isTurned, className = null }) {
  const turnTransitionProps = useSpring({ transform: `rotate(${isTurned ? 180 : 0}deg)` });

  return <AnimatedArrowDownIcon style={turnTransitionProps} className={className} />;
}

const AnimatedArrowDownIcon = animated(ArrowDownIcon);
